import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import { TextInput, SelectInput, CheckboxInput } from '@/components/common/FormField'
import api from '@/services/api'
import { departmentService } from '@/services/api'
import toast from 'react-hot-toast'

const INITIAL = { name: '', direction: '', is_active: true }

export default function ClassModal({ open, onClose, schoolClass = null, defaultDirection = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const isEdit = !!schoolClass

  const { data: directions } = useQuery({
    queryKey: ['directions-full'],
    queryFn: () => departmentService.directions({ page_size: 500 }).then((r) => r.data.results || r.data),
  })

  useEffect(() => {
    if (open) {
      setForm(schoolClass ? {
        name: schoolClass.name || '',
        direction: schoolClass.direction || '',
        is_active: schoolClass.is_active ?? true,
      } : {
        ...INITIAL,
        direction: defaultDirection || '',
      })
      setErrors({})
    }
  }, [open, schoolClass, defaultDirection])

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: val }))
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Обязательное поле'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const payload = { ...form }
      if (!payload.direction) payload.direction = null
      if (isEdit) {
        await api.patch(`/departments/classes/${schoolClass.id}/`, payload)
        toast.success('Группа обновлена')
      } else {
        await api.post('/departments/classes/', payload)
        toast.success('Группа создана')
      }
      qc.invalidateQueries(['departments-hierarchy'])
      qc.invalidateQueries(['classes-full'])
      qc.invalidateQueries(['classes'])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data && typeof data === 'object') {
        const fieldErrors = {}
        Object.entries(data).forEach(([k, v]) => {
          fieldErrors[k] = Array.isArray(v) ? v[0] : v
        })
        setErrors(fieldErrors)
        const nonField = data.detail || data.non_field_errors?.[0]
        if (nonField) toast.error(nonField)
      } else {
        toast.error('Ошибка сохранения')
      }
    } finally {
      setLoading(false)
    }
  }

  const dirOptions = (directions || []).map((d) => ({ value: d.id, label: `${d.name} (${d.department_name})` }))

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать группу' : 'Добавить группу'}
      onSubmit={handleSubmit}
      loading={loading}
    >
      <SelectInput
        label="Направление"
        value={form.direction}
        onChange={set('direction')}
        error={errors.direction}
        options={dirOptions}
        placeholder="Выберите направление"
      />
      <TextInput
        label="Название группы" required
        value={form.name}
        onChange={set('name')}
        error={errors.name}
        placeholder="ПИ-21"
      />
      <CheckboxInput
        label="Активная группа"
        checked={form.is_active}
        onChange={set('is_active')}
      />
    </ModalForm>
  )
}
